import React from "react";
import { Sparkles } from "lucide-react";
import type { TrainingCategoryKey } from "../../../lib/training";
import { gymConfig } from "../../../config/gymConfig";

type PBShareCardProps = {
  athleteName?: string;
  movement: string;
  metricType: string;
  value: string;
  unit?: string;
  dateLabel?: string;
  categoryLabel?: string;
  categoryKey?: TrainingCategoryKey | null;
};

function getPBShareTheme(categoryKey?: TrainingCategoryKey | null) {
  const badgeLabel =
    categoryKey === "strength"
      ? "Strength PB"
      : categoryKey === "power"
      ? "Power PB"
      : categoryKey === "engine"
      ? "Engine PB"
      : categoryKey === "personal"
      ? "Personal Best"
      : "New PB";

  const resultLabel =
    categoryKey === "strength"
      ? "Load"
      : categoryKey === "power"
      ? "Output"
      : categoryKey === "engine"
      ? "Time"
      : "Result";

  return {
    badgeLabel,
    glow: "radial-gradient(circle_at_top_right,rgba(59,130,246,0.14),transparent_32%)",
    ambientGlow:
      "radial-gradient(circle_at_bottom_left,rgba(96,165,250,0.10),transparent_40%)",
    line: "bg-[color:var(--training-accent-chart)]",
    pill: "border-[color:var(--training-accent-border)] bg-[color:var(--training-accent-soft)] text-[color:var(--training-accent-text)]",
    resultPanel:
      "border-[color:var(--training-accent-border)] bg-[color:var(--training-accent-soft)] shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]",
    resultLabel,
    categoryText: "text-[color:var(--training-accent-text)]",
    logoOpacity: "opacity-[0.22]",
  };
}

function splitMovementLabel(input: string) {
  const words = input.trim().split(/\s+/);

  if (words.length <= 2) {
    return [input];
  }

  const midpoint = Math.ceil(words.length / 2);
  return [
    words.slice(0, midpoint).join(" "),
    words.slice(midpoint).join(" "),
  ];
}

function isTimeLikeCategory(categoryKey?: TrainingCategoryKey | null) {
  return categoryKey === "engine";
}

export default function PBShareCard({
  athleteName,
  movement,
  metricType,
  value,
  unit,
  dateLabel,
  categoryLabel = gymConfig.training.benchmarkLabel,
  categoryKey,
}: PBShareCardProps) {
  const movementLines = splitMovementLabel(movement);
  const theme = getPBShareTheme(categoryKey);
  const isTimeLike = isTimeLikeCategory(categoryKey);

  return (
    <div
      className="relative w-[720px] overflow-hidden rounded-[40px] border border-[color:var(--color-border)] p-10 shadow-[0_24px_60px_rgba(0,0,0,0.45)]"
      style={{
        background:
          "linear-gradient(180deg, rgba(14,14,16,0.9), rgba(6,6,8,0.9))",
      }}
    >
      {/* background layers */}
      <div
        className="absolute inset-0 rounded-[40px]"
        style={{
          background: `linear-gradient(180deg, rgba(255,255,255,0.045), transparent 24%), ${theme.glow}, ${theme.ambientGlow}`,
        }}
      />

      <img
        src={gymConfig.assets.shareMarkUrl}
        alt=""
        aria-hidden="true"
        draggable={false}
        data-export-skip="true"
        className={`pointer-events-none absolute left-1/2 top-[58%] h-[400px] w-[600px] -translate-x-1/2 -translate-y-1/2 select-none object-contain ${theme.logoOpacity}`}
      />

      <div className="relative">
        {/* badge */}
        <div className="inline-flex items-center gap-2 rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-surface-overlay)] px-4 py-2">
          <Sparkles className="h-4 w-4 text-[color:var(--color-page-text)]" />
          <span className="text-[12px] font-semibold uppercase tracking-[0.2em] text-[color:var(--color-text-muted)]">
            {theme.badgeLabel}
          </span>
        </div>

        {/* category */}
        <div
          className={`mt-5 text-[11px] font-semibold uppercase tracking-[0.24em] ${theme.categoryText}`}
        >
          {categoryLabel}
        </div>

        {/* movement */}
        <h1 className="mt-4 text-[72px] font-black uppercase leading-[0.9] tracking-[-0.055em] text-[color:var(--color-page-text)]">
          {movementLines.map((line, index) => (
            <span key={`${line}-${index}`} className="block">
              {line}
            </span>
          ))}
        </h1>

        {/* accent + metric */}
        <div className={`flex items-center gap-4 ${isTimeLike ? "mt-4" : "mt-5"}`}>
          <div className={`h-[2px] w-14 rounded-full ${theme.line}`} />
          <div
            className={`inline-flex items-center rounded-full px-4 py-2 text-[14px] font-semibold uppercase tracking-[0.2em] ${theme.pill}`}
          >
            {metricType}
          </div>
        </div>

        {/* result */}
        <div className={`mt-8 ${isTimeLike ? "grid grid-cols-[1.2fr_0.8fr] gap-5" : "flex items-end justify-between gap-6"}`}>
          <div className={`rounded-[30px] border px-6 ${isTimeLike ? "py-6" : "flex-1 py-5"} ${theme.resultPanel}`}>
            {isTimeLike ? (
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[color:var(--color-text-subtle)]">
                  {theme.resultLabel}
                </div>
                <div className="mt-4 flex items-end leading-none">
                  <span className="text-[84px] font-black tracking-[-0.075em] text-[color:var(--color-page-text)]">
                    {value}
                  </span>
                  {unit ? (
                    <span className="ml-3 pb-[10px] text-[24px] font-semibold text-[color:var(--color-text-subtle)]">
                      {unit}
                    </span>
                  ) : null}
                </div>
                <div className="mt-4 h-px w-full bg-[color:var(--color-border)]" />
                <div className="mt-4 text-[12px] font-semibold uppercase tracking-[0.22em] text-[color:var(--color-text-subtle)]">
                  Precision beats pace drift
                </div>
              </div>
            ) : (
              <div className="flex items-end justify-between gap-6">
                <div className="flex items-end leading-none">
                  <span className="text-[76px] font-black tracking-[-0.065em] text-[color:var(--color-page-text)]">
                    {value}
                  </span>
                  {unit ? (
                    <span className="ml-2 pb-[8px] text-[26px] font-semibold text-[color:var(--color-text-subtle)]">
                      {unit}
                    </span>
                  ) : null}
                </div>

                <div className="text-right text-[11px] font-semibold uppercase tracking-[0.22em] text-[color:var(--color-text-subtle)]">
                  {theme.resultLabel}
                </div>
              </div>
            )}
          </div>

          {isTimeLike ? (
            <div className="rounded-[30px] border border-[color:var(--color-border)] bg-[color:var(--color-surface-overlay)] px-5 py-5">
              <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[color:var(--color-text-subtle)]">
                Benchmark
              </div>
              <div className="mt-2 text-[22px] font-bold leading-tight text-[color:var(--color-page-text)]">
                {movement}
              </div>
              <div className="mt-4 text-[11px] font-semibold uppercase tracking-[0.22em] text-[color:var(--color-text-subtle)]">
                Logged
              </div>
              <div className="mt-1 text-[16px] font-semibold text-[color:var(--color-text-muted)]">
                {dateLabel || "Today"}
              </div>
            </div>
          ) : null}
        </div>

        {/* footer */}
        <div className={`mt-8 border-t border-[color:var(--color-border)] pt-5 ${isTimeLike ? "flex items-end justify-between gap-6" : "grid grid-cols-[1fr_auto] gap-6"}`}>
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[color:var(--color-text-subtle)]">
              Athlete
            </div>
            <div className="mt-1 truncate text-[16px] font-semibold text-[color:var(--color-text-muted)]">
              {athleteName || gymConfig.brand.athleteLabel}
            </div>
          </div>

          {isTimeLike ? (
            <div className="shrink-0 text-right">
              <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[color:var(--color-text-subtle)]">
                Category
              </div>
              <div className="mt-1 text-[16px] font-semibold text-[color:var(--color-text-muted)]">
                {categoryLabel}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-6">
              <div className="min-w-0">
                <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[color:var(--color-text-subtle)]">
                  Metric
                </div>
                <div className="mt-1 truncate text-[16px] font-semibold text-[color:var(--color-text-muted)]">
                  {metricType}
                </div>
              </div>
              <div className="shrink-0 text-right">
                <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[color:var(--color-text-subtle)]">
                  Logged
                </div>
                <div className="mt-1 text-[16px] font-semibold text-[color:var(--color-text-muted)]">
                  {dateLabel || "Today"}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
